import {
  Bolt,
  Briefcase,
  Code2,
  Folder,
  Gift,
  Megaphone,
  PiggyBank,
  Plane,
  ShoppingBag,
  Users,
  Utensils,
  type LucideIcon,
} from "lucide-react";

import type { WalletIcon } from "../types/pockets-api.types";

/**
 * Color palette for pockets — mirrors `design/cards_v1.html` line 1394. The backend validates with
 * `@IsHexColor()` so any other hex would also be accepted, but the UI picker exposes only these 8 to keep
 * visuals cohesive across orgs.
 */
export const POCKET_COLOR_PALETTE: ReadonlyArray<string> = [
  "#0d8848", // brand green
  "#0284c7", // sky
  "#d97706", // amber
  "#7c3aed", // violet
  "#dc2626", // red
  "#0891b2", // cyan
  "#ea580c", // orange
  "#65a30d", // lime
];

export const DEFAULT_POCKET_COLOR = "#0d8848";

/**
 * Map every `WalletIcon` enum value (server-side) to a concrete lucide glyph + an i18n key for its label. The
 * order here drives the order of the picker grid in the create/edit modal.
 */
export type PocketIconDescriptor = {
  id: WalletIcon;
  icon: LucideIcon;
  labelKey: string;
};

export const POCKET_ICONS: ReadonlyArray<PocketIconDescriptor> = [
  { id: "default", icon: Folder, labelKey: "pocket_icon_default" },
  { id: "marketing", icon: Megaphone, labelKey: "pocket_icon_marketing" },
  { id: "operations", icon: Bolt, labelKey: "pocket_icon_operations" },
  { id: "saas", icon: Code2, labelKey: "pocket_icon_saas" },
  { id: "travel", icon: Plane, labelKey: "pocket_icon_travel" },
  { id: "shopping", icon: ShoppingBag, labelKey: "pocket_icon_shopping" },
  { id: "food", icon: Utensils, labelKey: "pocket_icon_food" },
  { id: "office", icon: Briefcase, labelKey: "pocket_icon_office" },
  { id: "team", icon: Users, labelKey: "pocket_icon_team" },
  { id: "gift", icon: Gift, labelKey: "pocket_icon_gift" },
  { id: "budget", icon: PiggyBank, labelKey: "pocket_icon_budget" },
];

export const DEFAULT_POCKET_ICON: WalletIcon = "default";

const ICON_BY_ID = new Map(POCKET_ICONS.map((d) => [d.id, d]));

/**
 * Resolves a `WalletIcon` enum value to its lucide component. Falls back to `Folder` (the `default` icon) for
 * unknown or missing values so the UI always has something to render.
 */
export const resolveIcon = (id?: WalletIcon | string | null): LucideIcon => {
  if (!id) return Folder;
  return ICON_BY_ID.get(id as WalletIcon)?.icon ?? Folder;
};

/** Convenience for components that need the display name (via i18n key) for a given icon id. */
export const resolveIconLabelKey = (id?: WalletIcon | string | null): string => {
  if (!id) return "pocket_icon_default";
  return ICON_BY_ID.get(id as WalletIcon)?.labelKey ?? "pocket_icon_default";
};

/**
 * Helper for components that read `pocket.metadata.pocket_name`. Falls back to a short identifier when the
 * name is empty/null so the listing is never blank.
 */
export const resolvePocketName = (pocket: {
  id: string;
  metadata?: { pocket_name?: string | null } | null;
}): string => {
  const name = pocket.metadata?.pocket_name?.trim();
  return name || `Pocket ${pocket.id.slice(0, 6)}`;
};
